import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';

interface SharePointStatusProps {
  vendorId: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedBy?: string;
  approvedDate?: any;
}

const SharePointStatus: React.FC<SharePointStatusProps> = ({ 
  vendorId, 
  status, 
  approvedBy, 
  approvedDate 
}) => {
  const [syncStatus, setSyncStatus] = useState<'unknown' | 'synced' | 'failed' | 'not-applicable'>('unknown');

  useEffect(() => {
    // Check if vendor is approved and should be synced
    if (status === 'approved') {
      // In a real implementation, you might check SharePoint for the vendor
      // For now, we'll assume it's synced if approved
      setSyncStatus('synced');
    } else if (status === 'rejected' || status === 'pending') {
      setSyncStatus('not-applicable');
    }
  }, [status, vendorId]);

  const getStatusIcon = () => {
    switch (syncStatus) {
      case 'synced':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'not-applicable':
        return <Clock className="w-4 h-4 text-gray-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusText = () => {
    switch (syncStatus) {
      case 'synced':
        return 'Synced to SharePoint';
      case 'failed':
        return 'Sync failed';
      case 'not-applicable':
        return status === 'pending' ? 'Pending approval' : 'Not synced';
      default:
        return 'Checking sync status...';
    }
  };

  const getStatusColor = () => {
    switch (syncStatus) {
      case 'synced':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'not-applicable':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  if (status === 'pending') {
    return null; // Don't show status for pending vendors
  }

  return (
    <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor()}`}>
      {getStatusIcon()}
      <span className="ml-1">{getStatusText()}</span>
    </div>
  );
};

export default SharePointStatus;

